import { ActionConfirmationModal } from "@/components/ui";
import { useCheckMatterContactConflict } from "@/lib/data/react-query/matters/matter-contact-connection";
import { useEffect, useRef, useState } from "react";

type Party = {
  contactId: string;
  label: string;
  matterId?: string;
};

export function useMatterConflictConfirmation(active: boolean) {
  const check = useCheckMatterContactConflict();
  const [party, setParty] = useState<Party | null>(null);
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null);
  const checkingRef = useRef(false);
  const activeRef = useRef(active);
  const generationRef = useRef(0);
  activeRef.current = active;

  const settle = (confirmed: boolean) => {
    resolveRef.current?.(confirmed);
    resolveRef.current = null;
    setParty(null);
  };

  useEffect(() => {
    if (!active) {
      generationRef.current += 1;
      resolveRef.current?.(false);
      resolveRef.current = null;
      setParty(null);
    }
    return () => {
      generationRef.current += 1;
      resolveRef.current?.(false);
      resolveRef.current = null;
    };
  }, [active]);

  const isCurrentCheck = (generation: number) =>
    activeRef.current && generationRef.current === generation;

  const confirmParty = async (proposed: Party): Promise<boolean> => {
    if (proposed.label !== "Client" && proposed.label !== "Defendant") {
      return true;
    }
    if (checkingRef.current || !activeRef.current) return false;
    const generation = generationRef.current;
    checkingRef.current = true;
    try {
      const result = await check.mutateAsync({
        contactId: proposed.contactId,
        label: proposed.label,
        ...(proposed.matterId ? { matterId: proposed.matterId } : {}),
      });
      if (!isCurrentCheck(generation)) return false;
      if (!result.data.hasConflictOfInterest) return true;
      setParty(proposed);
      return await new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
      });
    } catch {
      return false;
    } finally {
      checkingRef.current = false;
    }
  };

  const conflictModal = (
    <ActionConfirmationModal
      open={party !== null}
      setOpen={(open) => {
        if (!open) settle(false);
      }}
      title="Potential conflict of interest"
      description={`This contact is already a ${party?.label === "Client" ? "defendant" : "client"} on another matter in your firm. Adding them as a ${party?.label.toLowerCase() ?? "party"} may create a conflict of interest. Are you sure you want to continue?`}
      buttonText="Continue anyway"
      cancelText="Go back"
      actionVariant="default"
      afterActionFn={() => {
        settle(true);
      }}
      onCancel={() => {
        settle(false);
      }}
    />
  );

  return {
    confirmParty,
    conflictModal,
    isCheckingConflict: check.isPending || party !== null,
  };
}
